import { Injectable } from '@angular/core';
import { CompanyInterface } from '@app/interface/company';
import {
  InvoiceInterface,
  PensionFundCode,
  PaymentMethodCode,
} from '@app/interface/invoice';
import { KindCode } from '@app/interface/tax-withholding';
import { ProductServiceInterface } from '@app/interface/product-service';

export interface QuickInvoiceDraftProductLine {
  name: string;
  quantity: number;
  units: string;
  gross_amount: number;
  discount: number;
  description: string;
}

export interface QuickInvoiceDraftWithholding {
  kind: KindCode;
  rate_percentage: number;
}

export interface QuickInvoiceDraft {
  organizationId: number;
  company: CompanyInterface | null;
  progressiveNumber: number;
  enumeration: string;
  description: string;
  productLines: QuickInvoiceDraftProductLine[];
  withholdings: QuickInvoiceDraftWithholding[];
  revenueStampOption: 'none' | 'customer' | 'me';
  cassaPensionFundType: PensionFundCode;
  isPaperInvoice: boolean;
  isHealthcareInvoice: boolean;
  atecoCodeId: number | null;
  paymentMethod: PaymentMethodCode;
  emissionDate: string;
  paymentDays: number;
  addresseeCode: string;
  iban: string;
}

// Draft usato per duplicare una fattura esistente aprendo new-invoice già
// precompilata (invoice-detail "Duplica"), invece di creare direttamente una
// bozza via API: l'utente numero progressivo/validazione passano dal normale
// flusso di new-invoice, evitando i bug di numerazione della duplicazione lato backend.
export interface InvoiceDuplicateDraft {
  invoice: InvoiceInterface;
  productServices: ProductServiceInterface[];
  revenueStampOption: 'none' | 'customer' | 'me';
}

// Servizio "ponte" in-memory che trasporta i dati compilati nel quick-invoice
// bottom sheet (o in una fattura da duplicare) verso la pagina new-invoice,
// quando l'utente vuole "espandere"/duplicare nel flusso completo. Non passa
// per l'URL: i dati sono troppo annidati (righe prodotto, contributi) per i
// query params.
@Injectable({ providedIn: 'root' })
export class QuickInvoiceTransferService {
  private pendingDraft: QuickInvoiceDraft | null = null;
  private pendingInvoiceDuplicateDraft: InvoiceDuplicateDraft | null = null;

  setDraft(draft: QuickInvoiceDraft): void {
    this.pendingDraft = draft;
  }

  // Una tantum: il draft viene svuotato dopo la prima lettura, così non si
  // ri-applica a un refresh o a un ritorno successivo su new-invoice.
  consumeDraft(): QuickInvoiceDraft | null {
    const draft = this.pendingDraft;
    this.pendingDraft = null;
    return draft;
  }

  setInvoiceDuplicateDraft(draft: InvoiceDuplicateDraft): void {
    this.pendingInvoiceDuplicateDraft = draft;
  }

  consumeInvoiceDuplicateDraft(): InvoiceDuplicateDraft | null {
    const draft = this.pendingInvoiceDuplicateDraft;
    this.pendingInvoiceDuplicateDraft = null;
    return draft;
  }
}
